// src/features/rooms/components/RoomForm.jsx
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { FormSection, FormRow } from '../../../components/common/form';

const ROOM_CLASSIFICATIONS = [
  'UNCLASSIFIED',
  'SUPERVISED_AREA',
  'CONTROLLED_AREA',
];

const RoomForm = memo(function RoomForm({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  mode = 'create', // 'create' or 'edit'
}) {
  const { t } = useTranslation(['rooms', 'common']);

  const handleNameChange = useCallback((event) => {
    onInputChange('name', event.target.value);
  }, [onInputChange]);

  const handleAreaChange = useCallback((event) => {
    const value = event.target.value;
    // Allow empty or valid decimal numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      onInputChange('area_m2', value);
    }
  }, [onInputChange]);

  const handleClassificationChange = useCallback((event) => {
    onInputChange('classification', event.target.value);
  }, [onInputChange]);

  const handleNotesChange = useCallback((event) => {
    onInputChange('notes', event.target.value);
  }, [onInputChange]);

  return (
    <FormSection
      title={mode === 'create' ? t('rooms:form.room_information') : t('rooms:form.edit_room_information')}
    >
      <TextField
        fullWidth
        label={t('rooms:labels.room_name')}
        value={formData.name || ''}
        onChange={handleNameChange}
        error={!!validationErrors.name}
        helperText={validationErrors.name || t('rooms:form.name_helper')}
        required
        disabled={loading}
        placeholder={t('rooms:placeholders.name')}
        inputProps={{ maxLength: 255 }}
      />

      <FormRow>
        <TextField
          fullWidth
          type="number"
          label={t('rooms:labels.area_m2')}
          value={formData.area_m2 || ''}
          onChange={handleAreaChange}
          error={!!validationErrors.area_m2}
          helperText={validationErrors.area_m2 || t('rooms:form.area_helper')}
          required
          disabled={loading}
          placeholder="25.5"
          inputProps={{
            step: "0.01",
            min: "0",
          }}
        />
        <FormControl fullWidth required error={!!validationErrors.classification}>
          <InputLabel id="room-form-classification-label">{t('rooms:labels.classification')}</InputLabel>
          <Select labelId="room-form-classification-label"
            value={formData.classification || ''}
            onChange={handleClassificationChange}
            disabled={loading}
            label={t('rooms:labels.classification')}
          >
            {ROOM_CLASSIFICATIONS.map((classification) => (
              <MenuItem key={classification} value={classification}>
                {t(`rooms:classifications.${classification}`)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {validationErrors.classification || t('rooms:form.classification_helper')}
          </FormHelperText>
        </FormControl>
      </FormRow>

      <TextField
        fullWidth
        label={t('rooms:labels.notes')}
        value={formData.notes || ''}
        onChange={handleNotesChange}
        error={!!validationErrors.notes}
        helperText={validationErrors.notes || t('rooms:form.notes_helper')}
        disabled={loading}
        placeholder={t('rooms:placeholders.notes')}
        multiline
        rows={4}
      />

      {mode === 'create' && (
        <Alert severity="info">
          {t('rooms:form.after_creation_info')}
        </Alert>
      )}
    </FormSection>
  );
});

export default RoomForm;